import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Container,
} from "reactstrap";
import MetaTags from "react-meta-tags";
import { useParams } from "react-router-dom";
import { AxiosResponse } from "axios";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";

import Breadcrumbs from "../../components/common/Breadcrumb";
import { ApiCore } from "../../helpers/api_helper";
import { Loading } from "../../components/common/Loading";
import { Endpoints } from "../../utils/Endpoints";
import StudentProfileCard from "./StudentProfileCard";

interface AttendanceUser {
  id: number;
  name: string;
  email?: string;
  role?: string;
}

interface AttendanceRecord {
  id: number;
  student_id: number;
  date: string;
  category: string;
  meta?: any;
  created_at: string;
  year: number;
  term: string;
  user_id: number;
  studentInfo?: string;
  user?: AttendanceUser;
}

const request = new ApiCore();

const toLocalDateKey = (date: Date) => {
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
};

const formatDayLabel = (date: Date) =>
  date.toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatTime = (iso: string) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StudentAttendance = () => {
  const { id } = useParams() as { id: string };

  const [loadingStudent, setLoadingStudent] = useState<boolean>(true);
  const [student, setStudent] = useState<any>(null);

  const [loadingRecords, setLoadingRecords] = useState<boolean>(true);
  const [records, setRecords] = useState<AttendanceRecord[]>([]);

  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  useEffect(() => {
    request
      .get(`${Endpoints.students}/${id}`, {})
      .then((response: AxiosResponse) => {
        setStudent(response.data);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingStudent(false);
      });
  }, [id]);

  useEffect(() => {
    request
      .get(Endpoints.attendanceRecords, { params: { student_id: id } })
      .then((response: AxiosResponse) => {
        const list = Array.isArray(response.data) ? response.data : [];
        setRecords(list);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        setLoadingRecords(false);
      });
  }, [id]);

  const checkInByDate = useMemo(() => {
    const map: Record<string, AttendanceRecord> = {};
    records.forEach((record) => {
      if (record.category !== "check in") return;

      const existing = map[record.date];
      if (
        !existing ||
        new Date(record.created_at).getTime() <
          new Date(existing.created_at).getTime()
      ) {
        map[record.date] = record;
      }
    });
    return map;
  }, [records]);

  const attendedDays = useMemo(
    () => Object.keys(checkInByDate).sort((a, b) => b.localeCompare(a)),
    [checkInByDate]
  );

  useEffect(() => {
    if (attendedDays.length > 0) {
      const [yyyy, mm, dd] = attendedDays[0].split("-").map(Number);
      setSelectedDate(new Date(yyyy, mm - 1, dd));
    }
  }, [attendedDays]);

  const selectedKey = toLocalDateKey(selectedDate);
  const selectedRecord = checkInByDate[selectedKey];

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== "month") return null;
    return checkInByDate[toLocalDateKey(date)] ? "attendance-day" : null;
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <MetaTags>
          <title>Student Attendance</title>
        </MetaTags>

        <style>{`
          .attendance-calendar .react-calendar {
            border: 1px solid var(--bs-border-color);
            border-radius: 0.5rem;
            width: 100%;
            font-family: inherit;
          }
          .attendance-calendar .react-calendar__tile.attendance-day {
            background: var(--bs-primary);
            color: #fff;
            border-radius: 0.25rem;
          }
          .attendance-calendar .react-calendar__tile.attendance-day:enabled:hover,
          .attendance-calendar .react-calendar__tile.attendance-day:enabled:focus {
            background: var(--bs-primary);
            color: #fff;
            opacity: 0.9;
          }
          .attendance-calendar .react-calendar__tile--active {
            outline: 2px solid var(--bs-primary);
            outline-offset: -2px;
          }
        `}</style>

        <Container fluid>
          <Breadcrumbs title="Students" breadcrumbItem="Attendance" />

          <Row>
            <Col md={12}>
              <StudentProfileCard loading={loadingStudent} student={student} />
            </Col>
          </Row>

          <Row>
            <Col lg={6}>
              <Card>
                <CardHeader>
                  <CardTitle className="h5 mb-0">Attendance Calendar</CardTitle>
                </CardHeader>
                <CardBody>
                  {loadingRecords && <Loading label="Loading attendance..." />}
                  {!loadingRecords && (
                    <div className="attendance-calendar">
                      <Calendar
                        value={selectedDate}
                        onChange={(value: any) => {
                          if (value instanceof Date) {
                            setSelectedDate(value);
                          }
                        }}
                        tileClassName={tileClassName}
                      />
                      <div className="d-flex align-items-center mt-3 small text-muted">
                        <span
                          className="d-inline-block me-2 rounded"
                          style={{
                            width: 12,
                            height: 12,
                            backgroundColor: "var(--bs-primary)",
                          }}
                        ></span>
                        Days with check in ({attendedDays.length} day
                        {attendedDays.length === 1 ? "" : "s"})
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>

            <Col lg={6}>
              <Card>
                <CardHeader>
                  <CardTitle className="h5 mb-0">
                    {formatDayLabel(selectedDate)}
                  </CardTitle>
                </CardHeader>
                <CardBody>
                  {loadingRecords && <Loading label="Loading attendance..." />}
                  {!loadingRecords && !selectedRecord && (
                    <p className="text-muted mb-0">
                      No check in recorded on this day.
                    </p>
                  )}
                  {!loadingRecords && selectedRecord && (
                    <div className="d-flex align-items-start">
                      <div
                        className="rounded-circle bg-soft-primary text-primary d-inline-flex align-items-center justify-content-center me-3 flex-shrink-0"
                        style={{ width: 40, height: 40 }}
                      >
                        <i className="fas fa-check"></i>
                      </div>
                      <div className="flex-grow-1">
                        <div className="d-flex justify-content-between">
                          <span className="fw-semibold text-capitalize">
                            {selectedRecord.category}
                          </span>
                          <small className="text-muted">
                            {formatTime(selectedRecord.created_at)}
                          </small>
                        </div>
                        <small className="text-muted">
                          Term {selectedRecord.term} · {selectedRecord.year}
                        </small>
                        {selectedRecord.user?.name && (
                          <div className="small text-muted">
                            Recorded by {selectedRecord.user.name}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default StudentAttendance;
